// TODO: move all routes into app/AppRoutes file
import * as React from "react"
import { Route, Routes, useLocation, Navigate } from "react-router-dom"
import { LicenseManager } from "ag-grid-enterprise"
import { Helmet } from "react-helmet-async"
import APP_STATE from "constants/appState.js"
import { ChatbotProvider } from "@xogene/xogene-chatbot"
import getUrl from "api/apiMap"
import useDataDog from "utilities/useDataDog"
import { startSentry } from "utilities/sentry/sentry"

// pre-loaded components
import PersistLogin from "../components/PersistLogin/PersistLogin"
import RequireAuth from "components/RequireAuth/RequireAuth"
import Login from "../containers/auth/Login/Login"
import ForgotPassword from "../containers/auth/ForgotPassword/ForgotPassword"
import PasswordReset from "../containers/auth/PasswordReset/PasswordReset"
import AuthenticationLayout from "../templates/layouts/AutheticationLayout/AuthenticationLayout"
import MainGlobalLayout from "templates/layouts/MainGlobalLayout/MainGlobalLayout"
import useTokenExpirationNotifier from "utilities/useTokenExpirationNotifier/useTokenExpirationNotifier.jsx"
import Loading from "../components/Loading/Loading"
import ChatbotWrapper from "../components/ChatbotWrapper/ChatbotWrapper"
import ChatbotEmptyDrawer from "../components/ChatbotEmptyDrawer/ChatbotEmptyDrawer"

// styles
import "antd/dist/reset.css"
import "reactflow/dist/style.css"
// NOTE: no need to import ag-grid.css and ag-theme-quartz.css, b/c added in _ag-grid.scss
// NOTE: all SASS files are inside App.scss
import "./App.scss"

// context
import useAppState from "../context/hooks/useAppState"
import useChatbotStore from "../context/stores/useChatbotStore"
import useRefreshToken from "../utilities/useRefreshToken"

// api
import { useUserInfo } from "api/hooks"
import useAxiosPrivate from "api/hooks/useAxiosPrivate" // apis
import { useGetEnvVars, getPublicEnvVar } from "api/hooks/enum"

// navigation
import { STUDY_NAV_ROUTES } from "containers/studies/Study/navigation/navigation"
import requestNav from "containers/requests/Request/requestNav"
import usePreventClickJacking from "utilities/usePreventClickJacking"

// lazy loaded routes

const MyPrime = React.lazy(() => import("../containers/MyPrime/MyPrime"))

// requests
const Requests = React.lazy(() => import("containers/requests/Requests/Requests"))
const Request = React.lazy(() => import("containers/requests/Request/Request"))

// files
const Files = React.lazy(() => import("../containers/files/Files/Files"))
const File = React.lazy(() => import("containers/files/File/File"))

// studies
const Studies = React.lazy(() => import("../containers/studies/Studies/Studies"))
const Study = React.lazy(() => import("../containers/studies/Study/Study"))
const PrintStudyPage = React.lazy(() =>
  import("../containers/studies/Study/subcomponents/Authoring/subcomponents/PrintStudyPage/PrintStudyPage")
)
const Assessment = React.lazy(() => import("../containers/Assessment/Assessment"))
const CreateStudy = React.lazy(() => import("../containers/studies/CreateStudy/CreateStudy"))
const Activities = React.lazy(() => import("../containers/activities/Activities/Activities"))
const Activity = React.lazy(() => import("../containers/activities/Activity/Activity"))
const Actions = React.lazy(() => import("../containers/actions/Actions"))
const SiteManagement = React.lazy(() => import("../containers/SiteManagement/SiteManagement"))
const ProtocolMaintenance = React.lazy(() => import("../containers/ProtocolMaintenance/ProtocolMaintenance"))
const UploadHistory = React.lazy(() => import("../containers/UploadHistory/UploadHistory"))

// metrics
const ActivityChart = React.lazy(() => import("../containers/ActivityChart/ActivityChart"))
const ComplianceChart = React.lazy(() => import("../containers/ComplianceChart/ComplianceChart"))
const ActivityDashboard = React.lazy(() => import("../containers/ActivityDashboard/ActivityDashboard"))
const AssessmentDashboard = React.lazy(() => import("../containers/AssessmentDashboard/AssessmentDashboard"))
const CompletedActivities = React.lazy(() => import("../containers/CompletedActivities/CompletedActivities"))
const AssessmentsDue = React.lazy(() => import("../containers/AssessmentsDue/AssessmentsDue"))
const ActivityPlanningDashboard = React.lazy(() =>
  import("../containers/ActivityPlanningDashboard/ActivityPlanningDashboard")
)

// reports
const AllReports = React.lazy(() => import("../containers/AllReports/AllReports"))
const ActionsReport = React.lazy(() => import("../containers/reports/ActionsReport/ActionsReport"))
const ActivityReport = React.lazy(() => import("../containers/reports/ActivityReport/ActivityReport"))
const ActivityStepReport = React.lazy(() => import("../containers/reports/ActivityStepReport/ActivityStepReport"))
const AssessmentReport = React.lazy(() => import("../containers/reports/AssessmentReport/AssessmentReport"))
const AssessmentChangeReport = React.lazy(() =>
  import("../containers/reports/AssessmentChangeReport/AssessmentChangeReport")
)
const BottleneckReport = React.lazy(() => import("../containers/reports/BottleneckReport/BottleneckReport"))
const CommentReport = React.lazy(() => import("../containers/reports/CommentReport/CommentReport"))
const DataChangeReport = React.lazy(() => import("../containers/reports/DataChangeReport/DataChangeReport"))
const DataImportReport = React.lazy(() => import("../containers/reports/DataImportReport/DataImportReport"))
const IllogicalDataReport = React.lazy(() => import("../containers/reports/IllogicalDataReport/IllogicalDataReport"))
const MaintenanceReport = React.lazy(() => import("../containers/reports/MaintenanceReport/MaintenanceReport"))
const MaintenanceTriggerReport = React.lazy(() =>
  import("../containers/reports/MaintenanceTriggerReport/MaintenanceTriggerReport")
)
const NewRequestReport = React.lazy(() => import("../containers/reports/NewRequestReport/NewRequestReport"))
const NewStudyReport = React.lazy(() => import("../containers/reports/NewStudyReport/NewStudyReport"))
const ProtocolAmendmentReport = React.lazy(() =>
  import("../containers/reports/ProtocolAmendmentReport/ProtocolAmendmentReport")
)
const RequestReport = React.lazy(() => import("../containers/reports/RequestReport/RequestReport"))
const StudySiteReport = React.lazy(() => import("../containers/reports/StudySiteReport/StudySiteReport"))
const StudyReport = React.lazy(() => import("../containers/reports/StudyReport/StudyReport"))
const StudyDataReport = React.lazy(() => import("../containers/reports/StudyDataReport/StudyDataReport"))
const SubmissionsReport = React.lazy(() => import("../containers/reports/SubmissionsReport/SubmissionsReport"))

// admin pages
const MyPrimeAdmin = React.lazy(() => import("../containers/MyPrime/MyPrimeAdmin"))
const WorkflowBuilder = React.lazy(() => import("../containers/admin/WorkflowBuilder/WorkflowBuilder"))
const AssessmentTypes = React.lazy(() => import("../containers/admin/AssessmentTypes/AssessmentTypes"))
const Configurations = React.lazy(() => import("../containers/admin/Configurations/Configurations"))
const GridPresets = React.lazy(() => import("../containers/admin/GridPresets/GridPresets"))
const PRSSiteMaskingSettings = React.lazy(() =>
  import("../containers/admin/PRSSiteMaskingSettings/PRSSiteMaskingSettings")
)
const PRSLoginSettings = React.lazy(() => import("../containers/admin/PRSLoginSettings/PRSLoginSettings"))
const Users = React.lazy(() => import("../containers/admin/Users/Users"))
const AuditLog = React.lazy(() => import("../containers/admin/AuditLog/AuditLog"))

// account settings
const AccountSettings = React.lazy(() => import("../containers/AccountSettings/AccountSettings"))

const PageNotFound = React.lazy(() => import("../components/PageNotFound/PageNotFound"))

// standalone
const RequestReview = React.lazy(() => import("../containers/requests/RequestReview/RequestReview"))
const StudyReview = React.lazy(() => import("containers/studies/StudyReview/StudyReview"))

const App = () => {
  const location = useLocation()
  const { chatbotContext } = useChatbotStore()
  useDataDog()
  useAxiosPrivate()
  useTokenExpirationNotifier()
  usePreventClickJacking()
  const { data: envVars, isLoading: isEnvVarsLoading } = useGetEnvVars()
  const { getAccessToken, refreshTokens } = useRefreshToken()
  const [accessToken, setAccessToken] = React.useState(getAccessToken())
  const accessTokenRef = React.useRef(null)

  const { isLoading: isUserLoading } = useUserInfo()

  const { blurLoading, toastContextHolder, appState, setPublicEnvVars } = useAppState()

  // when the password expires redirect to sx login page
  React.useEffect(() => {
    getPublicEnvVar().then((res) => {
      setPublicEnvVars(res.data)
      startSentry(res.data)
    })
  }, [])

  React.useEffect(() => {
    if (envVars) {
      LicenseManager.setLicenseKey(envVars.REACT_APP_AG_GRID)
    }
  }, [envVars])

  React.useEffect(() => {
    if (appState === APP_STATE.authenticated) {
      setAccessToken(getAccessToken)
    }
  }, [appState])

  React.useEffect(() => {
    accessTokenRef.current = accessToken
  }, [accessToken])

  /** Light/dark mode */

  const [lightMode, setLightMode] = React.useState("light") // dark
  const winColorScheme = window.matchMedia("(prefers-color-scheme:light)")

  // add html attribute when light mode changes
  React.useEffect(() => {
    const root = document.querySelector("html")
    if (lightMode === "light") {
      root.setAttribute("data-prime-theme", "light")
    } else {
      root.setAttribute("data-prime-theme", "dark")
    }
  }, [lightMode])

  // callback for event listener to change light/dark modes
  const changeLightMode = React.useCallback((evt) => {
    setLightMode(evt.matches ? "light" : "dark")
  }, [])

  // // TODO: uncomment code below to implement dark mode
  // // event listener to change light/dark modes based on color scheme media query
  // React.useEffect(() => {
  //   winColorScheme.addEventListener("change", changeLightMode)
  //   return () => {
  //     winColorScheme.removeEventListener("change", changeLightMode)
  //   }
  // }, [winColorScheme, changeLightMode])

  // accessTokenRef.current = accessToken

  if (isUserLoading || isEnvVarsLoading || appState === APP_STATE.loading) return <Loading />

  return (
    <ChatbotProvider
      apiUrl={getUrl("/proxy/api/v1/ai")}
      workflowCode={envVars?.AI_WORKFLOW_CODE}
      tenant={envVars?.AI_WORKFLOW_TENANT}
      chatbotParams={{
        context: JSON.stringify(chatbotContext),
        doc_instruction_1: envVars?.AI_PRIME_QC_DOC_ID,
        vector_instruction_1: envVars?.AI_PRIME_QC_DOC_ID,
        doc_instruction_2: envVars?.AI_PRIME_ZENDESK_DOC_ID,
        vector_instruction_2: envVars?.AI_PRIME_ZENDESK_DOC_ID,
      }}
      token={accessTokenRef.current}
      hideFloatButton={true}
      EmptyDrawer={ChatbotEmptyDrawer}
      onRefreshToken={refreshTokens}
      chatbotName={envVars?.AI_PRIME_CHATBOT_NAME}
    >
      <Helmet>
        <title>Prime</title>
      </Helmet>
      {toastContextHolder}
      <React.Suspense fallback={<Loading />}>
        <div className={`prime-app${blurLoading ? " blur" : ""}`}>
          <Routes location={location}>
            <Route element={<AuthenticationLayout />}>
              <Route
                exact
                path="login"
                element={<Login />}
              />
              <Route
                exact
                path="forgot-password"
                element={<ForgotPassword />}
              />
              <Route
                exact
                path="password-reset"
                element={<PasswordReset />}
              />
            </Route>
            <Route element={<PersistLogin />}>
              <Route
                path="*"
                element={<RequireAuth />}
              >
                <Route element={<MainGlobalLayout />}>
                  {/* <Route path='*' element={<PageNotFound />} /> */}

                  {/* ------------- MY PRIME------------- */}
                  <Route
                    index
                    element={
                      <Navigate
                        replace
                        to="/my-prime"
                      />
                    }
                  />
                  <Route
                    path="my-prime"
                    element={<MyPrime />}
                  />

                  {/* ------------- REQUESTS ------------- */}
                  <Route
                    path="requests"
                    exact
                    element={<Requests />}
                  />
                  {/* ---------- request details ---------- */}
                  <Route
                    path="request/:requestId"
                    element={<Request />}
                  >
                    <Route
                      path=""
                      element={<Navigate to="overview" />}
                    />
                    {requestNav.map(({ key, ...route }) => (
                      <Route
                        key={key}
                        {...route}
                      />
                    ))}
                  </Route>
                  {/* -------- request file details -------- */}
                  <Route
                    path="request/:requestId/file/:fileId"
                    element={<File />}
                  />

                  {/* ------------- FILES ------------- */}
                  <Route
                    path="files"
                    exact
                    element={<Files />}
                  />
                  <Route
                    path="file/:fileId"
                    element={<File />}
                  />

                  {/* ------------- STUDIES ------------- */}
                  <Route
                    path="studies"
                    exact
                    element={<Studies />}
                  />
                  {/* ------------- study details ------------- */}
                  <Route
                    path="study/:studyId"
                    element={<Study />}
                  >
                    <Route
                      path=""
                      element={<Navigate to="overview" />}
                    />
                    {STUDY_NAV_ROUTES.map(({ key, ...route }) => (
                      <Route
                        key={key}
                        {...route}
                      />
                    ))}
                  </Route>
                  {/* ------------- assessment details ------------- */}
                  <Route
                    path="study/:studyId/assessment/:assessmentId"
                    element={<Assessment />}
                  />
                  <Route
                    path="create-study"
                    element={<CreateStudy />}
                  />
                  <Route
                    path="activities"
                    element={<Activities />}
                  />
                  {/* ------------- activity details ------------- */}
                  <Route
                    path="study/:studyId/activity/:activityId"
                    element={<Activity />}
                  />
                  <Route
                    path="action-items"
                    element={<Actions />}
                  />
                  <Route
                    path="site-management"
                    element={<SiteManagement />}
                  />
                  <Route
                    path="protocol-maintenance"
                    element={<ProtocolMaintenance />}
                  />
                  <Route
                    path="upload-history"
                    element={<UploadHistory />}
                  />

                  {/* ------------- METRICS------------- */}
                  <Route path="metrics">
                    <Route
                      path="activity-chart"
                      element={<ActivityChart />}
                    />
                    <Route
                      path="compliance-chart"
                      element={<ComplianceChart />}
                    />
                    <Route
                      path="activity-dashboard"
                      element={<ActivityDashboard />}
                    />
                    <Route
                      path="assessment-dashboard"
                      element={<AssessmentDashboard />}
                    />
                    <Route
                      path="completed-activities"
                      element={<CompletedActivities />}
                    />
                    <Route
                      path="assessments-due"
                      element={<AssessmentsDue />}
                    />
                    <Route
                      path="activity-planning"
                      element={<ActivityPlanningDashboard />}
                    />
                  </Route>

                  {/* ------------- ADMIN ------------- */}
                  <Route path="admin">
                    <Route
                      path="audit-log"
                      element={<AuditLog />}
                    />
                    <Route
                      path="my-prime-client"
                      element={<MyPrimeAdmin />}
                    />
                    <Route
                      path="workflow-builder"
                      element={<WorkflowBuilder />}
                    />
                    <Route
                      path="assessment-types"
                      element={<AssessmentTypes />}
                    />
                    <Route
                      path="configurations"
                      element={<Configurations />}
                    />
                    <Route
                      path="grid-presets"
                      element={<GridPresets />}
                    />
                    <Route
                      path="users"
                      element={<Users />}
                    />
                    <Route
                      path="prs-site-masking"
                      element={<PRSSiteMaskingSettings />}
                    />
                    <Route
                      path="prs-login-settings"
                      element={<PRSLoginSettings />}
                    />
                  </Route>

                  {/* ------------- REPORTS ------------- */}
                  {/* ----------- all reports ----------- */}
                  <Route
                    path="reports/all-reports"
                    element={<AllReports />}
                  />
                  <Route path="reports">
                    <Route
                      path="action-items"
                      element={<ActionsReport />}
                    />
                    <Route
                      path="activity"
                      element={<ActivityReport />}
                    />
                    <Route
                      path="activity-step"
                      element={<ActivityStepReport />}
                    />
                    <Route
                      path="maintenance"
                      element={<MaintenanceReport />}
                    />
                    <Route
                      path="assessment"
                      element={<AssessmentReport />}
                    />
                    <Route
                      path="assessment-change"
                      element={<AssessmentChangeReport />}
                    />
                    <Route
                      path="bottleneck"
                      element={<BottleneckReport />}
                    />
                    <Route
                      path="comment"
                      element={<CommentReport />}
                    />
                    <Route
                      path="data-change"
                      element={<DataChangeReport />}
                    />
                    <Route
                      path="data-import"
                      element={<DataImportReport />}
                    />
                    <Route
                      path="illogical-data"
                      element={<IllogicalDataReport />}
                    />
                    <Route
                      path="maintenance-trigger"
                      element={<MaintenanceTriggerReport />}
                    />
                    <Route
                      path="new-request"
                      element={<NewRequestReport />}
                    />
                    <Route
                      path="new-study"
                      element={<NewStudyReport />}
                    />
                    <Route
                      path="protocol-amendment"
                      element={<ProtocolAmendmentReport />}
                    />
                    <Route
                      path="request"
                      element={<RequestReport />}
                    />
                    <Route
                      path="study-data"
                      element={<StudyDataReport />}
                    />
                    <Route
                      path="study-site"
                      element={<StudySiteReport />}
                    />
                    <Route
                      path="study"
                      element={<StudyReport />}
                    />
                    <Route
                      path="submissions"
                      element={<SubmissionsReport />}
                    />
                  </Route>

                  {/* ------------- ACCOUNT SETTINGS ------------- */}
                  <Route
                    path="account-settings"
                    element={<AccountSettings />}
                  />

                  <Route
                    path="study/:studyId/study-data/:authoringId/print-page"
                    element={<PrintStudyPage />}
                  />

                  {/* ------------- PAGE NOT FOUND ------------- */}
                  <Route
                    path="*"
                    element={<PageNotFound />}
                  />
                </Route>
                {/* MainGlobalLayout */}

                {/* ------------- STUDY REVIEW ------------- */}
                <Route
                  path="study/:studyId/authoring-review"
                  element={<StudyReview />}
                />

                {/* ------------- REQUEST REVIEW ------------- */}
                <Route
                  path="request/:requestId/request-review"
                  element={<RequestReview />}
                />
              </Route>{" "}
              {/*RequireAuth*/}
            </Route>{" "}
            {/* PersistLogin */}
          </Routes>
          <ChatbotWrapper />
        </div>
      </React.Suspense>
      {blurLoading && <Loading />}
    </ChatbotProvider>
  )
}

export default App
